import { Link } from "react-router-dom";
import { Bug, AlertTriangle, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal, SectionHeading } from "@/components/Primitives";
import { PEST_GUIDE, SEASONAL_TIPS } from "@/data/site";

const dangerColor = { Structural: "bg-orange-100 text-orange-700", Health: "bg-red-100 text-red-700", Venomous: "bg-purple-100 text-purple-700", Sting: "bg-yellow-100 text-yellow-700" };

export default function PestGuide() {
  return (
    <div>
      <section className="pt-36 pb-16 bg-brand-green">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading light overline="Pest Identification Guide" title="Know your pest, know your risk"
            subtitle="Learn to identify common pests, understand the threat they pose, and see when they're most active." />
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PEST_GUIDE.map((p, i) => (
              <Reveal key={p.name} delay={(i % 3) * 0.06}>
                <div className="bg-white rounded-3xl p-7 border border-black/5 shadow-sm hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-brand-green/5 flex items-center justify-center"><Bug className="h-6 w-6 text-brand-green" /></div>
                    <Badge className={`${dangerColor[p.danger]} border-0 rounded-full font-semibold`}><AlertTriangle className="h-3 w-3 mr-1" />{p.danger}</Badge>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-brand-green mt-5">{p.name}</h3>
                  <p className="text-brand-muted text-sm leading-relaxed mt-2">{p.tip}</p>
                  <div className="flex items-center gap-2 text-brand-charcoal text-sm mt-4 pt-4 border-t border-black/5"><Calendar className="h-4 w-4 text-brand-gold" /> Active: {p.season}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center overline="Seasonal Prevention Tips" title="Stay ahead all year round" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {SEASONAL_TIPS.map((s, i) => (
              <Reveal key={s.season} delay={i * 0.08}>
                <div className="rounded-3xl p-7 border border-black/5 shadow-sm h-full" style={{ background: `${s.color}0d` }}>
                  <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: s.color }}><Calendar className="h-6 w-6 text-white" /></div>
                  <h3 className="font-heading text-xl font-bold text-brand-green mt-5">{s.season}</h3>
                  <ul className="mt-4 space-y-2">
                    {s.tips.map((t) => (<li key={t} className="text-sm text-brand-muted flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: s.color }} />{t}</li>))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/request-inspection"><Button className="rounded-full bg-brand-green text-white font-semibold h-13 px-8 hover:bg-brand-greenHover h-12">Get a Prevention Plan <ArrowRight className="h-5 w-5 ml-2" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
